/**
 * Kit splits API client (backend/routes/kitSplits.js, docs/memory/kit-splits.md).
 * Same shape as the other module clients: axios + getApiBaseUrl + credentials,
 * data unwrapped. Plus the pure index helpers the Products grid uses to find
 * a card's kit without a request per card.
 */

import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiClient';
import { isCccProduct, isPhaserProduct } from '../../utils/productStockEdit';

const BASE = () => `${getApiBaseUrl()}/api/kit-splits`;
const opts = { withCredentials: true };

export const KIT_SPLIT_MAX_CHILDREN = 6;
export const KIT_SPLIT_DEFAULT_FLAG_BELOW = 1;
export const KIT_SPLIT_DEFAULT_SPLIT_QTY = 5;
const MIN_LOOKUP_LENGTH = 2;

export const skuUpper = (sku) => String(sku || '').trim().toUpperCase();

// The two stores with a stock system of record (Phaser = Lightspeed, CCC =
// Qoblex). A twin link (kind 'mirror') joins one product from each.
export const STORE_DOMAINS = {
  phaser: 'phaser-fpv.myshopify.com',
  ccc: 'central-coast-coffee.myshopify.com'
};
export const STORE_LABELS = { phaser: 'PHASER', ccc: 'CCC' };
export const STORE_SYSTEMS = { phaser: 'Lightspeed', ccc: 'Qoblex' };

/** 'phaser' | 'ccc' | null for a myshopify domain (or a display name). */
export function storeKeyForDomain(domain) {
  const value = String(domain || '').trim().toLowerCase();
  if (!value) return null;
  if (/phaser/.test(value)) return 'phaser';
  if (/central[-_ ]?coast|^ccc$/.test(value)) return 'ccc';
  return null;
}

/** The other stock-system store: phaser ↔ ccc (null for anything else). */
export function otherStoreKey(storeKey) {
  if (storeKey === 'phaser') return 'ccc';
  if (storeKey === 'ccc') return 'phaser';
  return null;
}

/** "PHASER" / "CCC" / the domain without .myshopify.com. */
export function storeLabel(storeKeyOrDomain) {
  const key = STORE_LABELS[storeKeyOrDomain] ? storeKeyOrDomain : storeKeyForDomain(storeKeyOrDomain);
  return STORE_LABELS[key] || String(storeKeyOrDomain || '').replace(/\.myshopify\.com$/, '').toUpperCase();
}

/** Firestore id shape: `${store}__${PARENT_SKU_UPPER}`; the API's own id wins. */
export function kitIdFor(kit) {
  if (!kit) return null;
  if (kit.id) return kit.id;
  if (!kit.store || !kit.parentSku) return null;
  return `${kit.store}__${kit.parentSkuUpper || skuUpper(kit.parentSku)}`;
}

/** 'phaser' | 'ccc' | null, from a kit's storeKey or a product/store domain. */
export function kitStoreKeyFor({ kit, product, store } = {}) {
  if (kit?.storeKey) return kit.storeKey;
  const probe = product || { store: store || kit?.store };
  if (isPhaserProduct(probe)) return 'phaser';
  if (isCccProduct(probe)) return 'ccc';
  return null;
}

/** GET /api/kit-splits → { kits, capabilities }. */
export async function fetchKitSplits() {
  const res = await axios.get(BASE(), opts);
  return {
    kits: Array.isArray(res.data?.kits) ? res.data.kits : [],
    capabilities: res.data?.capabilities && typeof res.data.capabilities === 'object' ? res.data.capabilities : {}
  };
}

/**
 * PUT /api/kit-splits with
 * { store, parentSku, kind, children: [{ sku, qty, notPurchasable, store? }], flagBelow, splitQty, enabled, note, mergeStock? }
 * → { kit, task, links, engine, changeover, merge }. task = the urgent supervisor job the
 * save raised (a hold-kind "open N" job or the assembled changeover job) or null;
 * engine = the save-time engine run of an engine kit, or null; merge = the
 * twin link's "combine the two counts first" write ({ ok, from, to, added } |
 * { ok: false, error }) or null.
 */
export async function saveKitSplit(payload) {
  const res = await axios.put(BASE(), payload, opts);
  const data = res.data || {};
  const kit = data.kit || null;
  return {
    kit,
    task: data.task || null,
    links: data.links || null,
    engine: data.engine || (kit && engineActive(kit) ? kit.engine || null : null),
    changeover: data.changeover || kit?.changeover || null,
    merge: data.merge || null
  };
}

/**
 * GET /api/kit-splits/levels?store=&sku= → { sku, store, storeKey, system, current }:
 * the live Lightspeed / Qoblex level (the number the engine reads).
 */
export async function fetchKitLevel(store, sku) {
  const res = await axios.get(`${BASE()}/levels`, { ...opts, params: { store, sku } });
  return res.data || null;
}

/**
 * GET /api/kit-splits/lookup?warm=1: asks the server to load its catalogue
 * index now, so the first title search in the editor is not the slow one.
 * Fire-and-forget; failures are ignored.
 */
export function warmKitLookup() {
  return axios.get(`${BASE()}/lookup`, { ...opts, params: { warm: 1 } }).catch(() => null);
}

/** DELETE /api/kit-splits/:id → { ok: true } (the server closes the kit's open task). */
export async function deleteKitSplit(id) {
  const res = await axios.delete(`${BASE()}/${encodeURIComponent(id)}`, opts);
  return res.data;
}

/** GET /api/kit-splits/lookup?q=&store= → results[] (SKU / title typeahead). */
export async function lookupKitProducts(q, store) {
  const query = String(q || '').trim();
  if (query.length < MIN_LOOKUP_LENGTH) return [];
  const res = await axios.get(`${BASE()}/lookup`, {
    ...opts,
    params: { q: query, store: store || undefined }
  });
  return Array.isArray(res.data?.results) ? res.data.results : [];
}

/** Server `{ error }` text, else the transport message. */
export function readKitSplitError(err, fallback = 'Request failed') {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
}

const indexKey = (store, sku) => `${store}__${skuUpper(sku)}`;

/**
 * Map keyed `${store}__${SKU_UPPER}` → { asParent: Kit|null, asChild: [{ kit, child }] }.
 * Entries are built once per kits array, so a card's kitLink keeps its identity
 * (the ProductCard memo compares it by reference) until the kits are refetched.
 */
export function buildKitSplitIndex(kits) {
  const index = new Map();
  const entryFor = (key) => {
    let entry = index.get(key);
    if (!entry) {
      entry = { asParent: null, asChild: [] };
      index.set(key, entry);
    }
    return entry;
  };
  (Array.isArray(kits) ? kits : []).forEach((kit) => {
    if (!kit?.store || !kit.parentSku) return;
    entryFor(indexKey(kit.store, kit.parentSkuUpper || kit.parentSku)).asParent = kit;
    (Array.isArray(kit.children) ? kit.children : []).forEach((child) => {
      if (!child?.sku) return;
      // A twin link's one child (the root) lives in the OTHER store.
      const entry = entryFor(indexKey(child.store || kit.store, child.skuUpper || child.sku));
      // A part listed twice in one kit still reads as one membership.
      if (!entry.asChild.some((link) => link.kit === kit)) entry.asChild.push({ kit, child });
    });
  });
  return index;
}

/** The product's entry in the index, or null when it is in no kit. */
export function kitLinkFor(index, product) {
  if (!index || !product?.store || !product.sku || product.sku === 'N/A') return null;
  return index.get(indexKey(product.store, product.sku)) || null;
}

/** The kit a card's row opens: its own kit as parent, else the first kit it is part of. */
export function kitToEdit(kitLink) {
  if (!kitLink) return null;
  return kitLink.asParent || kitLink.asChild?.[0]?.kit || null;
}

/** "28619 ×1 · 27131 ×1" */
export function formatKitChildren(kit) {
  return (Array.isArray(kit?.children) ? kit.children : [])
    .map((child) => `${child.sku} ×${Number(child.qty) || 1}`)
    .join(' · ');
}

// ------------------------------------------------------------------ kinds
// docs/memory/kit-stock-engine.md: assembled = kit stock is "how many we can
// make"; broken = the parts that cannot be bought carry the kit count; hold =
// the original "open N kits" job.

export const KIT_KINDS = [
  {
    value: 'assembled',
    label: 'Assembled from parts',
    short: 'Assembled',
    hint: 'We stock only the parts. Kit stock = how many we can make; selling a kit takes the parts.'
  },
  {
    value: 'broken',
    label: 'Broken for parts',
    short: 'Broken for parts',
    hint: 'We stock the kit. Parts we cannot buy count the kits too; a part order past the shelf breaks a kit.'
  },
  {
    value: 'hold',
    label: 'Hold both',
    short: 'Hold both',
    hint: 'We stock kit and parts. A job says "open N kits" when a part runs low.'
  },
  {
    value: 'mirror',
    label: 'Twin in the other store',
    short: 'Twin',
    hint: 'The same item listed on both sites. The twin copies the root\'s stock; a sale of the twin takes one from the root.'
  }
];
const KIND_VALUES = new Set(KIT_KINDS.map((k) => k.value));
/** Kinds whose stock the engine writes (everything but the hold job). */
export const ENGINE_KINDS = new Set(['assembled', 'broken', 'mirror']);

/** 'assembled' | 'broken' | 'hold' | 'mirror' (a kit saved before kinds existed is 'hold'). */
export function kitKind(kit) {
  return KIND_VALUES.has(kit?.kind) ? kit.kind : 'hold';
}

// ------------------------------------------------------------- twin links
// kind 'mirror' (docs/memory/kit-stock-engine.md "Twin in the other store"):
// the kit's parent is the TWIN (the copy, in kit.store); its one child is
// the ROOT, counted in its own system, in the other store (child.store).

/** The root of a twin link: { sku, store, storeKey, title, imageUrl } or null. */
export function kitRoot(kit) {
  if (kitKind(kit) !== 'mirror') return null;
  const child = (Array.isArray(kit?.children) ? kit.children : []).find((c) => c?.sku);
  if (!child) return null;
  const storeKey = child.storeKey || storeKeyForDomain(child.store) || otherStoreKey(kit.storeKey || storeKeyForDomain(kit.store));
  return {
    sku: child.sku,
    store: child.store || (storeKey ? STORE_DOMAINS[storeKey] : null),
    storeKey,
    title: child.title || '',
    imageUrl: child.imageUrl || null
  };
}

/** The twin side of a twin link (its parent): { sku, store, storeKey, title, imageUrl }. */
export function kitTwin(kit) {
  if (kitKind(kit) !== 'mirror') return null;
  return {
    sku: kit.parentSku,
    store: kit.store,
    storeKey: kit.storeKey || storeKeyForDomain(kit.store),
    title: kit.parentTitle || '',
    imageUrl: kit.parentImageUrl || null
  };
}

/**
 * This product's side of a twin link: 'twin' (its stock is copied), 'root'
 * (the counted one) or null when the link does not involve it.
 */
export function twinRoleFor(kitLink, product) {
  if (!kitLink || !product?.sku) return null;
  const own = kitLink.asParent;
  if (own && kitKind(own) === 'mirror') return 'twin';
  const link = (kitLink.asChild || []).find((l) => l?.kit && kitKind(l.kit) === 'mirror');
  return link ? 'root' : null;
}

/** The twin link a card belongs to (as twin or root), or null. */
export function twinKitFor(kitLink) {
  if (!kitLink) return null;
  if (kitLink.asParent && kitKind(kitLink.asParent) === 'mirror') return kitLink.asParent;
  return (kitLink.asChild || []).find((l) => l?.kit && kitKind(l.kit) === 'mirror')?.kit || null;
}

/** The engine's level key for a kit member: plain SKU in the kit's store, `STORE:SKU` elsewhere. */
export function engineLevelKey(kit, { sku, storeKey }) {
  const kitStore = kit?.storeKey || storeKeyForDomain(kit?.store);
  const key = skuUpper(sku);
  return storeKey && storeKey !== kitStore ? `${String(storeKey).toUpperCase()}:${key}` : key;
}

/** The KIT_KINDS entry for a kit or a kind value (unknown → hold). */
export function kitKindInfo(kitOrKind) {
  const value = typeof kitOrKind === 'string'
    ? (KIND_VALUES.has(kitOrKind) ? kitOrKind : 'hold')
    : kitKind(kitOrKind);
  return KIT_KINDS.find((k) => k.value === value);
}

/** True while an assembled kit's one-off "open all N kits" job is still open. */
export function changeoverPending(kit) {
  return kit?.changeover?.status === 'pending';
}

/**
 * Whether the stock engine runs for this kit: the server's `engine.active`
 * when present, else derived (assembled / broken, enabled, no changeover pending).
 */
export function engineActive(kit) {
  if (!kit) return false;
  if (typeof kit.engine?.active === 'boolean') return kit.engine.active;
  return ENGINE_KINDS.has(kitKind(kit)) && kit.enabled !== false && !changeoverPending(kit);
}

/** "A", "A or B", "A, B or C" (joiner "or" / "and"). */
function joinList(items, joiner) {
  const list = items.filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  return `${list.slice(0, -1).join(', ')} ${joiner} ${list[list.length - 1]}`;
}

const perKit = (child) => {
  const n = Number(child?.qty);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
};

const positiveIntOr = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback;
};

/**
 * The editor's live sentence: what the app will do with this kit, per kind.
 * kit = { kind, parentSku, children: [{ sku, qty, title?, notPurchasable }],
 * flagBelow, splitQty, enabled }. With `withTitles` (the hover) each SKU is
 * followed by its title from the child, `childTitles` (SKU_UPPER → title) or
 * `parentTitle`, so the visible line stays short.
 */
export function kitSentence(kit, { parentTitle = '', childTitles = null, withTitles = false } = {}) {
  const parentSku = String(kit?.parentSku || '').trim() || 'this kit';
  const children = (Array.isArray(kit?.children) ? kit.children : [])
    .filter((c) => c && String(c.sku || '').trim());
  if (!children.length) return 'Add the parts this kit is made of.';
  const named = (sku, title) => (withTitles && title ? `${sku} (${title})` : sku);
  const kitName = named(parentSku, parentTitle);
  const partName = (c) => {
    const sku = String(c.sku).trim();
    return named(sku, c.title || childTitles?.[skuUpper(sku)] || '');
  };
  const off = kit?.enabled === false ? ' (paused while Enabled is off)' : '';
  const kind = kitKind(kit);

  if (kind === 'mirror') {
    const root = children[0];
    const twinStore = kit?.storeKey || storeKeyForDomain(kit?.store);
    const rootStore = root.storeKey || storeKeyForDomain(root.store) || otherStoreKey(twinStore);
    const twinLabel = storeLabel(twinStore);
    const rootLabel = storeLabel(rootStore);
    const system = STORE_SYSTEMS[rootStore] || 'its own system';
    return `${kitName} (${twinLabel}) stock = ${partName(root)} (${rootLabel}) stock; a ${twinLabel} sale takes one from ${String(root.sku).trim()} in ${system}${off}`;
  }

  if (kind === 'assembled') {
    const takes = children.length > 1 && children.every((c) => perKit(c) === 1)
      ? 'one of each'
      : joinList(children.map((c) => (perKit(c) === 1 ? `one ${partName(c)}` : `${perKit(c)} × ${partName(c)}`)), 'and');
    return `${kitName} stock = how many we can make from ${joinList(children.map(partName), 'and')}; selling one takes ${takes}${off}`;
  }

  if (kind === 'broken') {
    const carrying = children.filter((c) => c.notPurchasable);
    const leftovers = children.filter((c) => !c.notPurchasable);
    if (!carrying.length) {
      return `Tick "Cannot be bought" on the part that only comes from breaking ${kitName}; until then nothing is counted or broken${off}`;
    }
    const qtys = [...new Set(carrying.map(perKit))];
    let sum;
    if (qtys.length > 1) sum = `shelf + what each ${kitName} in stock holds`;
    else sum = `shelf + ${qtys[0] !== 1 ? `${qtys[0]} × ` : ''}${kitName} in stock`;
    let rest = `an order past the shelf breaks a ${kitName}`;
    if (leftovers.length === 1) rest += ` and the leftover ${partName(leftovers[0])} goes to its location`;
    else if (leftovers.length > 1) rest += ` and the leftovers (${leftovers.map(partName).join(', ')}) go to their locations`;
    return `${joinList(carrying.map(partName), 'and')} stock = ${sum}; ${rest}${off}`;
  }

  const flagBelow = positiveIntOr(kit?.flagBelow, KIT_SPLIT_DEFAULT_FLAG_BELOW);
  const splitQty = positiveIntOr(kit?.splitQty, KIT_SPLIT_DEFAULT_SPLIT_QTY);
  const qtys = [...new Set(children.map(perKit))];
  let gain;
  if (children.length === 1) gain = `+${splitQty * perKit(children[0])}`;
  else if (qtys.length === 1) gain = `+${splitQty * qtys[0]} of each`;
  else gain = children.map((c) => `+${splitQty * perKit(c)} × ${String(c.sku).trim()}`).join(', ');
  return `When ${joinList(children.map(partName), 'or')} drops below ${flagBelow}, the supervisor gets a job to open ${splitQty} × ${kitName} (${gain})${off}`;
}

const levelOf = (levels, sku) => {
  if (!levels || !sku) return null;
  const n = levels[skuUpper(sku)];
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
const signed = (n) => (n < 0 ? `−${Math.abs(n)}` : String(n));

/**
 * How a card's stock number is made up, from the kit engine's last read
 * levels (`kit.engine.levels`, SKU_UPPER → number):
 * - assembled kit → { kind: 'assembled', parts, text: '= min(25428: 100, 27213: 100)', short: '= min(100, 100)' }
 * - carrying part (broken kit, "cannot be bought") → { kind: 'carrying', total,
 *   shelf, inKits, kitSku, text: '19 = 3 on shelf + 16 in 30544', short: '3 shelf + 16 in kits' }
 * - anything else, an engine that is not running, or a missing level → null.
 */
export function kitStockBreakdown(kitLink, product) {
  if (!kitLink) return null;
  const own = kitLink.asParent;
  if (own && kitKind(own) === 'mirror') {
    // The twin's number is a copy of the root's: "= 24577 on PHASER".
    if (!engineActive(own)) return null;
    const root = kitRoot(own);
    if (!root) return null;
    const level = levelOf(own.engine?.levels, engineLevelKey(own, root));
    const label = storeLabel(root.storeKey);
    return {
      kind: 'mirror',
      rootSku: root.sku,
      rootStoreKey: root.storeKey,
      level,
      text: `= ${root.sku} on ${label}${level === null ? '' : ` (${signed(level)})`}`,
      short: `= ${label} ${root.sku}`
    };
  }
  if (own && kitKind(own) === 'assembled') {
    if (!engineActive(own)) return null;
    const levels = own.engine?.levels;
    const parts = (Array.isArray(own.children) ? own.children : [])
      .filter((c) => c?.sku)
      .map((c) => ({ sku: c.sku, qty: perKit(c), level: levelOf(levels, c.skuUpper || c.sku) }));
    if (!parts.length || parts.some((p) => p.level === null)) return null;
    const term = (p, withSku) => `${withSku ? `${p.sku}: ` : ''}${signed(p.level)}${p.qty !== 1 ? ` ÷ ${p.qty}` : ''}`;
    return {
      kind: 'assembled',
      parts,
      text: `= min(${parts.map((p) => term(p, true)).join(', ')})`,
      short: `= min(${parts.map((p) => term(p, false)).join(', ')})`
    };
  }
  const links = Array.isArray(kitLink.asChild) ? kitLink.asChild : [];
  const carrying = links.find((l) => l?.kit && l.child?.notPurchasable
    && kitKind(l.kit) === 'broken' && engineActive(l.kit));
  if (!carrying) return null;
  const { kit, child } = carrying;
  const levels = kit.engine?.levels;
  const total = levelOf(levels, child.skuUpper || child.sku || product?.sku);
  const kitLevel = levelOf(levels, kit.parentSkuUpper || kit.parentSku);
  if (total === null || kitLevel === null) return null;
  const inKits = kitLevel * perKit(child);
  const shelf = total - inKits;
  const op = inKits < 0 ? '−' : '+';
  return {
    kind: 'carrying',
    total,
    shelf,
    inKits,
    kitSku: kit.parentSku,
    text: `${signed(total)} = ${signed(shelf)} on shelf ${op} ${Math.abs(inKits)} in ${kit.parentSku}`,
    short: `${signed(shelf)} shelf ${op} ${Math.abs(inKits)} in kits`
  };
}

// --------------------------------------------------------- part locations
// An active assembled kit has no shelf of its own: it is picked as its parts.
// The backend writes `partLocations` ({ SKU_UPPER: string|null }) and
// `partLocationsText` ("W-02-E-13 + V-04-E-11") on the kit doc; a doc saved
// before that has neither.

// Shopify stores an empty location as the literal "Not Set".
const cleanLocation = (value) => {
  const text = String(value ?? '').trim();
  return text && text.toLowerCase() !== 'not set' ? text : null;
};

const kitChildren = (kit) => (Array.isArray(kit?.children) ? kit.children : []).filter((c) => c?.sku);

/** [{ sku, location: string|null }] per part, or null when the doc has no `partLocations`. */
export function kitPartLocations(kit) {
  const map = kit?.partLocations;
  if (!map || typeof map !== 'object') return null;
  return kitChildren(kit).map((c) => ({
    sku: c.sku,
    location: cleanLocation(map[skuUpper(c.skuUpper || c.sku)])
  }));
}

/**
 * The card's location text for an assembled kit: the backend's
 * `partLocationsText`, else built from `partLocations` ("A + B", each place
 * once), else '' (the card then reads "not set").
 */
export function kitPartLocationsText(kit) {
  const text = typeof kit?.partLocationsText === 'string' ? kit.partLocationsText.trim() : '';
  if (text) return text;
  const parts = kitPartLocations(kit) || [];
  return [...new Set(parts.map((p) => p.location).filter(Boolean))].join(' + ');
}

/**
 * "Picked as parts: 25428 at W-02-E-13 · 27213 at V-04-E-11. The pair has no
 * shelf of its own." ("The kit" past two parts; "(location not set)" per
 * unlocated part; an older doc says the locations are not loaded yet.)
 */
export function kitPartLocationsHover(kit) {
  const children = kitChildren(kit);
  const tail = `${children.length === 2 ? 'The pair' : 'The kit'} has no shelf of its own.`;
  const parts = kitPartLocations(kit);
  if (!parts) {
    const skus = children.map((c) => c.sku).join(' + ');
    return `Picked as parts${skus ? ` (${skus})` : ''}; their locations are not loaded yet. ${tail}`;
  }
  if (!parts.length) return `Picked as parts. ${tail}`;
  const list = parts
    .map((p) => `${p.sku} ${p.location ? `at ${p.location}` : '(location not set)'}`)
    .join(' · ');
  return `Picked as parts: ${list}. ${tail}`;
}
